import { initialDocumentState } from "../states/templateStates.js";
import { BedrockRuntimeClient, InvokeModelWithBidirectionalStreamCommand, ConverseCommand } from "@aws-sdk/client-bedrock-runtime";

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

function encodeEvent(eventObj) {
    const jsonStr = JSON.stringify(eventObj);
    return {
        chunk: {
            bytes: new TextEncoder().encode(jsonStr)
        }
    };
}

function toReadableStream(asyncIterable) {
    const iterator = asyncIterable[Symbol.asyncIterator]();
    return new ReadableStream({
        async pull(controller) {
            try {
                const { value, done } = await iterator.next();
                if (done) {
                    controller.close();
                } else {
                    controller.enqueue(value);
                }
            } catch (e) {
                controller.error(e);
            }
        }
    });
}

export class NovaSonicClient {
    constructor() {
        this.state = "idle";
        this.documentState = structuredClone(initialDocumentState);
        this.audioContext = null;
        this.processor = null;
        this.inputSource = null;
        this.bedrockClient = null;
        this.mediaStream = null;
        this.isRecording = false;
        this.currentTranscript = "";
        this.promptName = "prompt-1";
        this.audioContentName = "user-audio";
    }

    async connect() {
        try {
            const res = await fetch(`${SERVER_URL}/api/bedrock/sts`);
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || "Failed to get STS session token");
            }
            const tempCreds = await res.json();

            this.bedrockClient = new BedrockRuntimeClient({
                region: process.env.REACT_APP_AWS_REGION || "us-east-1",
                credentials: {
                    accessKeyId: tempCreds.accessKeyId,
                    secretAccessKey: tempCreds.secretAccessKey,
                    sessionToken: tempCreds.sessionToken
                }
            });
            this.isConnected = true;
            if (this.onStateChange) this.onStateChange("connected");
        } catch (error) {
            console.error("Connection failed:", error);
            this.isConnected = false;
            if (this.onStateChange) this.onStateChange("error");
        }
    }

    disconnect() {
        this.stopListening();
        this.bedrockClient = null;
        this.isConnected = false;
        if (this.onStateChange) this.onStateChange("disconnected");
    }

    async startListening(systemPrompt) {
        if (!this.isConnected) await this.connect();
        if (!this.isConnected) return;

        this.currentTranscript = "";

        try {
            this.isRecording = true;

            this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            this.audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
            this.inputSource = this.audioContext.createMediaStreamSource(this.mediaStream);
            this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);

            const audioQueue = [];

            this.processor.onaudioprocess = (e) => {
                const raw = e.inputBuffer.getChannelData(0);
                const pcm = new Int16Array(raw.length);
                for (let i = 0; i < raw.length; i++) {
                    const s = Math.max(-1, Math.min(1, raw[i]));
                    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                }
                const bytes = new Uint8Array(pcm.buffer);
                let bin = '';
                for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
                audioQueue.push(btoa(bin));
            };

            this.inputSource.connect(this.processor);
            this.processor.connect(this.audioContext.destination);

            this.runBedrockStream(audioQueue, systemPrompt || "You are a helpful assistant.");

            if (this.onStateChange) this.onStateChange("listening");
        } catch (err) {
            this.stopListening();
        }
    }

    async runBedrockStream(queue, systemPromptText) {
        const self = this;

        const events = {
            [Symbol.asyncIterator]: async function* () {
                yield encodeEvent({
                    event: {
                        sessionStart: {
                            inferenceConfiguration: {
                                maxTokens: 1024,
                                topP: 0.9,
                                temperature: 0.7
                            }
                        }
                    }
                });

                yield encodeEvent({
                    event: {
                        promptStart: {
                            promptName: self.promptName,
                            textOutputConfiguration: { mediaType: "text/plain" },
                            audioOutputConfiguration: {
                                mediaType: "audio/lpcm",
                                sampleRateHertz: 24000,
                                sampleSizeBits: 16,
                                channelCount: 1,
                                voiceId: "tiffany"
                            }
                        }
                    }
                });

                yield encodeEvent({
                    event: {
                        contentStart: {
                            promptName: self.promptName,
                            contentName: "system-text",
                            type: "TEXT",
                            role: "SYSTEM",
                            textInputConfiguration: { mediaType: "text/plain" }
                        }
                    }
                });
                yield encodeEvent({
                    event: {
                        textInput: {
                            promptName: self.promptName,
                            contentName: "system-text",
                            content: systemPromptText
                        }
                    }
                });
                yield encodeEvent({
                    event: {
                        contentEnd: {
                            promptName: self.promptName,
                            contentName: "system-text"
                        }
                    }
                });

                yield encodeEvent({
                    event: {
                        contentStart: {
                            promptName: self.promptName,
                            contentName: self.audioContentName,
                            type: "AUDIO",
                            role: "USER",
                            audioInputConfiguration: {
                                mediaType: "audio/lpcm",
                                sampleRateHertz: 16000,
                                sampleSizeBits: 16,
                                channelCount: 1
                            }
                        }
                    }
                });

                while (self.isRecording) {
                    if (queue.length > 0) {
                        const b64 = queue.shift();
                        yield encodeEvent({
                            event: {
                                audioInput: {
                                    promptName: self.promptName,
                                    contentName: self.audioContentName,
                                    content: b64
                                }
                            }
                        });
                    } else {
                        await new Promise(r => setTimeout(r, 20));
                    }
                }

                await new Promise(r => setTimeout(r, 500));

                while (queue.length > 0) {
                    const b64 = queue.shift();
                    yield encodeEvent({
                        event: {
                            audioInput: {
                                promptName: self.promptName,
                                contentName: self.audioContentName,
                                content: b64
                            }
                        }
                    });
                }

                yield encodeEvent({ event: { contentEnd: { promptName: self.promptName, contentName: self.audioContentName } } });
                yield encodeEvent({ event: { promptEnd: { promptName: self.promptName } } });
                yield encodeEvent({ event: { sessionEnd: {} } });
            }
        };

        let fullResponse = "";
        try {
            const bodyStream = toReadableStream(events);

            const command = new InvokeModelWithBidirectionalStreamCommand({
                modelId: "amazon.nova-sonic-v1:0",
                body: bodyStream
            });

            const response = await this.bedrockClient.send(command);

            let currentRole = null;
            for await (const evt of response.body) {
                if (evt.chunk && evt.chunk.bytes) {
                    try {
                        const text = new TextDecoder().decode(evt.chunk.bytes);
                        const parsed = JSON.parse(text);

                        if (parsed.event?.contentStart) {
                            currentRole = parsed.event.contentStart.role;
                        }

                        if (parsed.event?.textOutput?.content && currentRole === "ASSISTANT") {
                            fullResponse += parsed.event.textOutput.content;
                        }

                        if (parsed.event?.contentEnd) {
                            if (parsed.event.contentEnd.stopReason === "END_TURN") {
                                break;
                            }
                            currentRole = null;
                        }
                    } catch (_) {
                        // non-JSON chunk
                    }
                }
            }

            if (fullResponse) {
                if (this.onResponseCallback) this.onResponseCallback(fullResponse);
            } else {
                if (this.onResponseCallback) this.onResponseCallback("");
            }

        } catch (e) {
            if (this.onResponseCallback) this.onResponseCallback(fullResponse || "");
        }
    }

    stopListening() {
        this.isRecording = false;

        setTimeout(() => {
            if (this.processor) { this.processor.disconnect(); this.processor = null; }
            if (this.inputSource) { this.inputSource.disconnect(); this.inputSource = null; }
            if (this.audioContext) { this.audioContext.close(); this.audioContext = null; }
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach(t => t.stop());
                this.mediaStream = null;
            }
        }, 500);
    }

    onAudio(callback) { this.onAudioCallback = callback; }
    onResponse(callback) { this.onResponseCallback = callback; }
    onState(callback) { this.onStateChange = callback; }
}

export class NovaProVisionClient {
    async analyzeTemplateImage(imageBase64, imageType = 'png') {
        try {
            const prompt = this.getAnalysisPrompt();
            const res = await fetch(`${SERVER_URL}/api/bedrock/vision`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imageBase64, imageType, prompt })
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || "Vision analysis failed.");
            }

            const { text } = await res.json();

            try {
                const jsonMatch = text.match(/\{[\s\S]*\}/);
                const jsonString = jsonMatch ? jsonMatch[0] : text;
                let cfg = JSON.parse(jsonString);

                // Run post-processing heuristics to fix common AI misclassifications
                cfg = this.postProcessConfig(cfg);

                return {
                    id: `user-template-${Date.now()}`, name: 'Custom Template', source: 'user-upload',
                    layout: cfg.layout || { type: 'sidebar-left', sidebarWidth: '35%', sidebarColor: '#2c3e50', mainBackground: '#ffffff' },
                    colors: cfg.colors || { headerColor: '#3498db', accentColor: '#2980b9', textColor: '#2c3e50' },
                    fonts: cfg.fonts || { headingFontFamily: "'Inter', sans-serif", bodyFontFamily: "'Inter', sans-serif", headingFontSize: '1.5rem', textAlignment: 'left' },
                    sections: cfg.sections || this.defaultSections(),
                    profilePhoto: cfg.profilePhoto || null,
                    style: { fontFamily: cfg.fonts?.bodyFontFamily || cfg.style?.fontFamily || "'Inter', sans-serif", headerColor: cfg.colors?.headerColor || '#3498db', accentColor: cfg.colors?.accentColor || '#2980b9' }
                };
            } catch (e) {
                throw new Error("AI returned invalid layout data. Please try uploading the image again.");
            }
        } catch (error) {
            throw new Error(error.message || "Template analysis failed. Please try again.");
        }
    }

    defaultSections() {
        return [
            { id: 'summary', heading: 'Professional Summary', content: 'Your professional summary here.' },
            { id: 'experience', heading: 'Work Experience', content: 'Your work experience details here.' },
            { id: 'education', heading: 'Education', content: 'Your educational background.' },
            { id: 'skills', heading: 'Skills', content: 'Your key skills and competencies.' }
        ];
    }

    getFallbackConfig() {
        return {
            id: `user-template-${Date.now()}`, name: 'Custom Template', source: 'user-upload',
            layout: { type: 'sidebar-left', sidebarWidth: '35%', sidebarColor: '#2c3e50', mainBackground: '#ffffff' },
            colors: { headerColor: '#3498db', accentColor: '#2980b9', textColor: '#2c3e50' },
            fonts: { headingFontFamily: "'Inter', sans-serif", bodyFontFamily: "'Inter', sans-serif", headingFontSize: '1.5rem', textAlignment: 'left' },
            sections: this.defaultSections(),
            style: { fontFamily: "'Inter', sans-serif", headerColor: '#3498db', accentColor: '#2980b9' }
        };
    }

    getAnalysisPrompt() {
        return `You are an expert resume layout analyst. Carefully analyze this resume template image. Extract the exact layout structure, typographic details, fine-grained section level styling, and precise color hex codes observed in the image.

STEP 1 — LAYOUT DETECTION (most important):
Look at the image and answer these questions BEFORE writing JSON:
- Is there a vertical column on the LEFT or RIGHT side that has a DIFFERENT background color than the main content area? If YES → the layout is "sidebar-left" or "sidebar-right".
- Is there a horizontal banner at the TOP spanning the full width with a colored background? If YES → include "headerBackground".
- If neither sidebar nor header banner exists → the layout is "centered".

CRITICAL SIDEBAR RULES:
- A sidebar is ANY vertical column (left or right) that is visually distinct from the main body. It may contain: contact info, skills, education, languages, a profile photo, or any combination.
- The sidebar color is the BACKGROUND COLOR of that column. Extract it precisely (e.g. #3D2B1F for dark brown, #1e3a5f for navy). It is NEVER transparent if the column is visually colored.
- Common sidebar sections include: Contact, Skills, Education, Languages, Interests, Certifications. If these appear in a visually separate column, they belong in "sidebar".
- If the resume has TWO distinct visual columns (regardless of content), it HAS a sidebar. Do not classify it as "centered".

STEP 2 — SECTION COLUMN ASSIGNMENT:
- For EACH section, determine which visual column it appears in:
  - Sections in the colored/shaded column → "column": "sidebar"
  - Sections in the white/main area → "column": "main"
- This is critical. Do NOT put all sections in "main" if some are clearly in the sidebar column of the image.

STEP 3 — COLOR EXTRACTION:
- Sample colors DIRECTLY from the image pixels. Do not guess or use generic colors.
- "headerColor" = the color of section heading text (like "ABOUT ME", "EXPERIENCE") in the MAIN body area.
- "accentColor" = the color of section heading text in the SIDEBAR, or a secondary highlight color.
- "sidebarColor" = the background fill of the sidebar column.
- If section headings use the SAME color as the sidebar background, that is fine — just report what you see accurately.

Respond ONLY with valid JSON in this exact structure, containing NO markdown formatting:
{
  "layout": { 
    "type": "header-top | sidebar-left | sidebar-right | centered | two-column", 
    "sidebarWidth": "35%", 
    "sidebarColor": "hex code for sidebar background if it is shaded/colored (e.g. #3D2B1F). Must be 'transparent' ONLY if that column is pure white.", 
    "headerBackground": "hex code for the top horizontal banner if it exists. Must be 'transparent' if white or no banner.",
    "mainBackground": "exact hex code for main background (e.g. #FFFFFF)",
    "borderRadius": "exact border radius for image/containers (e.g. 50% or 8px or 0px)",
    "dividerStyles": "any divider line styling observed (e.g. 2px solid #e5e7eb or none)"
  },
  "profilePhoto": {
    "detected": true,
    "position": "sidebar | header | top-left | top-right | center",
    "shape": "circle | rounded | square",
    "size": "80px | 100px | 120px etc.",
    "borderColor": "hex code of any visible border around the photo, or 'none'",
    "borderWidth": "e.g. 3px or 0px"
  },
  "colors": { 
    "headerColor": "exact hex code for section heading text in the main body area", 
    "accentColor": "exact hex code for secondary highlights or sidebar heading text", 
    "textColor": "exact hex code for normal body text (e.g. #333333)",
    "headerTextColor": "exact hex code for the person's name text"
  },
  "fonts": {
    "headingFontFamily": "font family observed for headings (e.g. 'Merriweather', serif)",
    "bodyFontFamily": "font family observed for body text (e.g. 'Inter', sans-serif)",
    "headingFontSize": "estimated font size for headings like 'Experience' (e.g. 1.25rem or 18px)",
    "textAlignment": "left | center | right | justify"
  },
  "sections": [ 
    {
      "id": "summary", 
      "heading": "About Me",
      "column": "main",
      "styles": {
        "padding": "e.g. 10px 0",
        "marginBottom": "e.g. 15px",
        "textTransform": "e.g. uppercase for the heading"
      },
      "content": "Dummy layout content simulating the structure seen in the image."
    },
    {
      "id": "skills", 
      "heading": "Skills",
      "column": "sidebar",
      "styles": {
        "padding": "0", "marginBottom": "20px", "textTransform": "none"
      },
      "content": "• Skill 1\\n• Skill 2\\n• Skill 3"
    }
  ]
}

FINAL CHECKLIST:
1. Did you correctly identify whether there is a sidebar? If the image has two visual columns, it MUST be sidebar-left or sidebar-right.
2. Did you assign each section to the correct column ("main" or "sidebar") based on where it visually appears?
3. Did you extract REAL hex colors from the image, not generic web colors?
4. For "profilePhoto": If the template has a visible profile photo, headshot, avatar, or placeholder, set "detected" to true. If none exists, set the entire field to null.
5. Do NOT include the person's name, job title, or contact info as sections. Only body sections like Profile, Experience, Skills, Languages, etc.
6. Your response must be purely the JSON object, starting with { and ending with }.`;
    }

    postProcessConfig(cfg) {
        const hasSidebarSections = cfg.sections?.some(s => s.column === 'sidebar');
        const layoutType = cfg.layout?.type;
        const sidebarColor = cfg.layout?.sidebarColor;

        // If sections are tagged as sidebar but layout says centered, fix the layout
        if (hasSidebarSections && (layoutType === 'centered' || !layoutType)) {
            cfg.layout.type = 'sidebar-left';
            if (!sidebarColor || sidebarColor === 'transparent') {
                cfg.layout.sidebarColor = cfg.colors?.accentColor || '#2c3e50';
            }
        }

        // If layout is sidebar but sidebarColor is missing/transparent, 
        // try to infer it from accent or header colors
        if ((layoutType === 'sidebar-left' || layoutType === 'sidebar-right') 
            && (!sidebarColor || sidebarColor === 'transparent' || sidebarColor === '#ffffff' || sidebarColor === '#FFFFFF')) {
            // Check if any section has sidebar column — if so, there must be a visible sidebar
            if (hasSidebarSections) {
                cfg.layout.sidebarColor = cfg.colors?.accentColor || cfg.colors?.headerColor || '#2c3e50';
            }
        }

        // Ensure sidebar sections like skills/education/languages default to sidebar column
        // if the layout is sidebar-* but no sections were tagged correctly
        if ((layoutType === 'sidebar-left' || layoutType === 'sidebar-right') && !hasSidebarSections) {
            const sidebarCandidates = ['skills', 'education', 'languages', 'interests', 'certifications', 'hobbies', 'references'];
            cfg.sections?.forEach(section => {
                if (sidebarCandidates.includes(section.id?.toLowerCase())) {
                    section.column = 'sidebar';
                }
            });
        }

        return cfg;
    }
}

export const novaSonic = new NovaSonicClient();
export const novaProVision = new NovaProVisionClient();
